import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetAllCandidatesCardsInfiniteQuery } from "../../rq/hooks/useQueries";
import Card from "../Card/Card";
import { FetchDiv, NoCard, TypoFetch } from "../Card/styles";
import CardSkeleton from "../CardSkeleton/CardSkeleton";
import CircularIndeterminate from "../CircularIndeterminate/CircularIndeterminate";
import SearchVideos from "../SearchVideos/SearchVideos";
import { professions } from "../../lib/constants";

export default function SearchCandidate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchWord = searchParams.get("sW");
  const originalInput: any = searchParams.get("oI") || searchWord;

  const [search, setSearch] = useState<any>(originalInput);
  const [isSearching, setIsSearching] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  // Copy of translation function from CoverSection
  const handleTranslation = async (text: string): Promise<string> => {
    if (!text.trim()) return text;

    try {
      // Clean input text - remove extra spaces, lowercase
      const cleanedText = text.toLowerCase().trim();

      // Minimum similarity threshold (0-1, where 1 is exact match)
      const SIMILARITY_THRESHOLD = 0.5;

      // Calculate string similarity using Levenshtein distance
      const calculateSimilarity = (a: string, b: string): number => {
        if (a.length === 0) return b.length;
        if (b.length === 0) return a.length;

        // Simple implementation of Levenshtein distance
        const matrix: number[][] = [];

        // Initialize matrix
        for (let i = 0; i <= b.length; i++) {
          matrix[i] = [i];
        }

        for (let j = 0; j <= a.length; j++) {
          matrix[0][j] = j;
        }

        // Fill matrix
        for (let i = 1; i <= b.length; i++) {
          for (let j = 1; j <= a.length; j++) {
            const cost = a[j - 1] === b[i - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
              matrix[i - 1][j] + 1, // deletion
              matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j - 1] + cost // substitution
            );
          }
        }

        // Calculate similarity score (0-1)
        const distance = matrix[b.length][a.length];
        const maxLength = Math.max(a.length, b.length);
        return 1 - distance / maxLength;
      };

      // Find best matching profession
      let bestMatch = {
        profession: null as any,
        similarity: 0,
        language: "",
      };

      // Check French professions
      for (const prof of professions.french) {
        const similarity = calculateSimilarity(
          cleanedText,
          prof.name.toLowerCase()
        );
        if (similarity > bestMatch.similarity) {
          bestMatch = { profession: prof, similarity, language: "french" };
        }
      }

      // Check Dutch professions
      for (const prof of professions.dutch) {
        const similarity = calculateSimilarity(
          cleanedText,
          prof.name.toLowerCase()
        );
        if (similarity > bestMatch.similarity) {
          bestMatch = { profession: prof, similarity, language: "dutch" };
        }
      }

      // If we found a match with sufficient similarity
      if (
        bestMatch.profession &&
        bestMatch.similarity >= SIMILARITY_THRESHOLD
      ) {
        // Find the English equivalent using the ID
        const englishEquivalent = professions.english.find(
          (prof) => prof.id === bestMatch.profession.id
        );

        if (englishEquivalent) return englishEquivalent.name;
      }

      return text;
    } catch (error) {
      console.error("Translation attempt failed:", error);
      return text;
    }
  };

  const handleSearch = async (newSearchTerm: string) => {
    if (!newSearchTerm.trim()) return;

    try {
      setIsTranslating(true);
      const translatedSearchWord = await handleTranslation(newSearchTerm);

      navigate(
        `/candidate/search?sW=${encodeURIComponent(
          translatedSearchWord
        )}&oI=${encodeURIComponent(newSearchTerm)}`
      );
    } catch (error) {
      console.error("Search error:", error);
      navigate(`/candidate/search?sW=${encodeURIComponent(newSearchTerm)}`);
    } finally {
      setIsTranslating(false);
    }
  };

  const {
    refetch,
    status,
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetAllCandidatesCardsInfiniteQuery(searchWord, {
    getNextPageParam: (lastPage: any, pages: any) => {
      if (lastPage.metaData.pageNumber < lastPage.metaData.pageCount) {
        return lastPage.metaData.pageNumber + 1;
      }
    },
  });

  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  useEffect(() => {
    (async () => {
      if (searchWord) {
        setIsSearching(true);
        await refetch();
        setIsSearching(false);
      } else {
        await refetch();
        setIsSearching(false);
      }
    })();
  }, [searchWord]);

  useEffect(() => {
    if (originalInput !== search) {
      handleSearch(search);
    }
    if (!search) {
      // Clear the search parameters from URL when search is empty
      navigate("/candidate/search", { replace: true });
    }
  }, [search]);

  const { t } = useTranslation();

  return (
    <>
      <SearchVideos
        setSearch={setSearch}
        results={data?.pages[0].metaData?.totalItemCount ?? 0}
        isSearching={isSearching || isTranslating}
        search={search}
        onSearch={() => handleSearch(search)}
      />
      {status === "loading" ? (
        <CircularIndeterminate />
      ) : (
        <>
          {data?.pages.map((card, i) => (
            <Fragment key={i}>
              {card.data.length > 0 ? (
                card.data.map((l, index) => (
                  <Card
                    key={index}
                    profilePicture={l.profilePicture}
                    candidateUserId={l.candidateUserId}
                    candidateId={l.candidateId}
                    professions={l.profession}
                    businessId={l.businessId}
                    businessUserId={l.businessUserId}
                    name={l.name}
                    lastName={l.lastName}
                    country={l.country}
                    hasCv={l.hasCv ?? false}
                    cvPath={l.cvPath ?? null}
                    video={l.video}
                    videoViews={l.videoViews}
                    likes={l.likes}
                    saves={l.saves}
                    isMyFavorite={l.isMyFavorite}
                    likedFromMe={l.likedFromMe}
                    videoId={l.videoId}
                    boughtFromCurrentUser={l.boughtFromCurrentUser}
                    email={l.email}
                    phoneNumber={l.phoneNumber}
                    refetch={refetch}
                  />
                ))
              ) : (
                <NoCard>{t("card.no_cards")}</NoCard>
              )}
            </Fragment>
          ))}
          {data && data?.pages[0].data.length > 0 && (
            <FetchDiv ref={ref} onClick={() => fetchNextPage()}>
              {isFetchingNextPage ? (
                <CardSkeleton />
              ) : hasNextPage ? (
                <TypoFetch>{t("card.search.load_more")}</TypoFetch>
              ) : (
                <TypoFetch>{t("card.search.nothing_more")}</TypoFetch>
              )}
            </FetchDiv>
          )}
        </>
      )}
    </>
  );
}
